 
/* eslint-disable @typescript-eslint/no-unused-vars */
 
 
 
 
 
import React, { useMemo } from 'react';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import type { Product } from '@smartphone-tycoon/shared-core';
import { PlaceholderText } from '../../ui/Placeholder';

interface MarketingCostsProps {
  products: Product[];
  reputation: number;
  monthlyRevenue: number;
  companySize: 'startup' | 'small' | 'medium' | 'large';
}

export function MarketingCosts({ products, reputation, monthlyRevenue, companySize }: MarketingCostsProps) {
  // Calculate marketing costs based on Finance view's 15% of total costs
  const marketingCostBreakdown = useMemo(() => {
    // Base marketing spend as percentage of revenue (industry standard: 5-15%)
    const marketingPercentage = 0.15; // 15% as shown in FinanceView
    const monthlyMarketingBudget = monthlyRevenue * marketingPercentage;
    const dailyMarketingBudget = monthlyMarketingBudget / 30;
    
    // Break down marketing spend by category (placeholder percentages)
    const digitalMarketing = monthlyMarketingBudget * 0.40; // TODO: Make configurable
    const traditionalMedia = monthlyMarketingBudget * 0.25; // TODO: Make configurable
    const retailPromotion = monthlyMarketingBudget * 0.20; // TODO: Make configurable
    const publicRelations = monthlyMarketingBudget * 0.10; // TODO: Make configurable
    const tradeShows = monthlyMarketingBudget * 0.05; // TODO: Make configurable
    
    return {
      monthlyTotal: monthlyMarketingBudget,
      dailyTotal: dailyMarketingBudget,
      digitalMarketing,
      traditionalMedia,
      retailPromotion,
      publicRelations,
      tradeShows
    };
  }, [monthlyRevenue]);

  // Calculate per-product marketing costs
  const productMarketingCosts = useMemo(() => {
    const activeProducts = products.filter(p => p.status === 'production');
    
    return activeProducts.map(product => {
      // Allocate marketing budget based on product age and performance
      const monthsSinceRelease = product.releaseDate 
        ? Math.max(1, Math.floor((Date.now() - new Date(product.releaseDate.year, product.releaseDate.month - 1, product.releaseDate.day).getTime()) / (30 * 24 * 60 * 60 * 1000)))
        : 1;
      
      // New products get more marketing spend
      const ageMultiplier = Math.max(0.5, 2 / monthsSinceRelease);
      
      // Higher margin products get more marketing
      const profitMargin = (product.price - product.productionCost) / product.price;
      const marginMultiplier = 1 + profitMargin;
      
      // Base allocation (equal split)
      const baseAllocation = marketingCostBreakdown.monthlyTotal / Math.max(1, activeProducts.length);
      const adjustedAllocation = baseAllocation * ageMultiplier * marginMultiplier;
      
      return {
        product,
        monthlyMarketing: adjustedAllocation,
        dailyMarketing: adjustedAllocation / 30,
        ageMultiplier,
        marginMultiplier,
        monthsSinceRelease
      };
    });
  }, [products, marketingCostBreakdown.monthlyTotal]);

  // Calculate campaign costs
  const campaignCosts = useMemo(() => {
    const baseCampaignCost = {
      startup: 10000,  // TODO: Make campaign costs configurable
      small: 25000,    // TODO: Make campaign costs configurable
      medium: 50000,   // TODO: Make campaign costs configurable
      large: 100000    // TODO: Make campaign costs configurable
    };
    
    return {
      productLaunch: baseCampaignCost[companySize] * 2, // TODO: Make multiplier configurable
      brandAwareness: baseCampaignCost[companySize],
      seasonal: baseCampaignCost[companySize] * 0.75, // TODO: Make multiplier configurable
      competitive: baseCampaignCost[companySize] * 1.5 // TODO: Make multiplier configurable
    };
  }, [companySize]);

  // Calculate ROI metrics - commented out as it's not currently used
  // const _marketingROI = useMemo(() => {
  //   const reputationValue = reputation * 1000; // TODO: Make reputation value configurable
  //   const marketingEfficiency = marketingCostBreakdown.monthlyTotal > 0 
  //     ? (monthlyRevenue / marketingCostBreakdown.monthlyTotal) 
  //     : 0;
  //   
  //   return {
  //     reputationValue,
  //     marketingEfficiency,
  //     costPerReputationPoint: marketingCostBreakdown.monthlyTotal / Math.max(1, reputation),
  //     revenuePerMarketingDollar: marketingEfficiency
  //   };
  // }, [reputation, monthlyRevenue, marketingCostBreakdown.monthlyTotal]);

  return (
    <div className="space-y-6">
      {/* Marketing Spend Breakdown */}
      <div className="bg-theme-bg-content rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold text-theme-text-primary">Marketing Spend by Channel</h3>
        </div>
        <div className="p-4">
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <div>
                <p className="font-medium text-theme-text-primary">Digital Marketing</p>
                <p className="text-sm text-theme-text-muted">Online ads, social media, SEO</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-theme-text-primary">{formatCurrency(marketingCostBreakdown.digitalMarketing)}/month</p>
                <p className="text-sm text-theme-text-muted"><PlaceholderText text="40% of budget" /></p>
              </div>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <div>
                <p className="font-medium text-theme-text-primary">Traditional Media</p>
                <p className="text-sm text-theme-text-muted">TV, radio, print advertising</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-theme-text-primary">{formatCurrency(marketingCostBreakdown.traditionalMedia)}/month</p>
                <p className="text-sm text-theme-text-muted"><PlaceholderText text="25% of budget" /></p>
              </div>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <div>
                <p className="font-medium text-theme-text-primary">Retail Promotion</p>
                <p className="text-sm text-theme-text-muted">In-store displays, demos</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-theme-text-primary">{formatCurrency(marketingCostBreakdown.retailPromotion)}/month</p>
                <p className="text-sm text-theme-text-muted"><PlaceholderText text="20% of budget" /></p>
              </div>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <div>
                <p className="font-medium text-theme-text-primary">Public Relations</p>
                <p className="text-sm text-theme-text-muted">Press releases, media outreach</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-theme-text-primary">{formatCurrency(marketingCostBreakdown.publicRelations)}/month</p>
                <p className="text-sm text-theme-text-muted"><PlaceholderText text="10% of budget" /></p>
              </div>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <div>
                <p className="font-medium text-theme-text-primary">Trade Shows & Events</p>
                <p className="text-sm text-theme-text-muted">Industry events, conferences</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-theme-text-primary">{formatCurrency(marketingCostBreakdown.tradeShows)}/month</p>
                <p className="text-sm text-theme-text-muted"><PlaceholderText text="5% of budget" /></p>
              </div>
            </div>
            <div className="flex justify-between items-center py-2 font-bold">
              <p>Total Marketing Spend</p>
              <p>{formatCurrency(marketingCostBreakdown.monthlyTotal)}/month</p>
            </div>
          </div>
        </div>
      </div>

      {/* Product Marketing Allocation */}
      {productMarketingCosts.length > 0 && (
        <div className="bg-theme-bg-content rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold text-theme-text-primary">Marketing Budget by Product</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-theme-border">
              <thead className="bg-theme-bg-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                    Age (months)
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                    Age Factor
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                    Margin Factor
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                    Monthly Budget
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                    % of Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-theme-bg-content divide-y divide-theme-border">
                {productMarketingCosts.map(({ product, monthlyMarketing, monthsSinceRelease, ageMultiplier, marginMultiplier }) => (
                  <tr key={product.id}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-theme-text-primary">{product.name}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-theme-text-primary">
                      {monthsSinceRelease}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-theme-text-primary">
                      {ageMultiplier.toFixed(2)}x
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-theme-text-primary">
                      {marginMultiplier.toFixed(2)}x
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium text-theme-text-primary">
                      {formatCurrency(monthlyMarketing)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-theme-text-primary">
                      {((monthlyMarketing / marketingCostBreakdown.monthlyTotal) * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-theme-bg-secondary">
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-right font-medium text-theme-text-primary">
                    Total Allocated
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-theme-text-primary">
                    {formatCurrency(productMarketingCosts.reduce((sum, p) => sum + p.monthlyMarketing, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-theme-text-primary">
                    100%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Campaign Cost Estimates */}
      <div className="bg-theme-bg-content rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold text-theme-text-primary">Campaign Cost Estimates</h3>
          <p className="text-sm text-theme-text-muted mt-1">Based on company size: {companySize}</p>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-theme-text-primary mb-2">Product Launch Campaign</h4>
              <p className="text-2xl font-bold text-theme-text-primary"><PlaceholderText text={formatCurrency(campaignCosts.productLaunch)} /></p>
              <p className="text-sm text-theme-text-muted mt-1">Full marketing blitz for new products</p>
            </div>
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-theme-text-primary mb-2">Brand Awareness Campaign</h4>
              <p className="text-2xl font-bold text-theme-text-primary"><PlaceholderText text={formatCurrency(campaignCosts.brandAwareness)} /></p>
              <p className="text-sm text-theme-text-muted mt-1">Build company reputation</p>
            </div>
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-theme-text-primary mb-2">Seasonal Campaign</h4>
              <p className="text-2xl font-bold text-theme-text-primary"><PlaceholderText text={formatCurrency(campaignCosts.seasonal)} /></p>
              <p className="text-sm text-theme-text-muted mt-1">Holiday and seasonal promotions</p>
            </div>
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-theme-text-primary mb-2">Competitive Response</h4>
              <p className="text-2xl font-bold text-theme-text-primary"><PlaceholderText text={formatCurrency(campaignCosts.competitive)} /></p>
              <p className="text-sm text-theme-text-muted mt-1">Counter competitor launches</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}